import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Slack, Search, Check, Loader2, X, Link2Off, CheckCircle2, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

export default function ConnectSlackDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [link, setLink] = useState(null);
  const [defaults, setDefaults] = useState([]);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [saving, setSaving] = useState(null);
  const [savingDefaults, setSavingDefaults] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [s, m, d] = await Promise.all([
        axios.get(`${API}/api/slack/link`, { headers: authHeaders() }),
        axios.get(`${API}/api/slack/members`, { headers: authHeaders() }),
        axios.get(`${API}/api/slack/default-members`, { headers: authHeaders() }),
      ]);
      setLink(s.data.link || null);
      setMembers(m.data.members || []);
      setDefaults(d.data.member_ids || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load Slack members');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setQ(''); setDq(''); load(); } }, [open, load]);

  const saveDefaults = async (ids) => {
    setSavingDefaults(true);
    const prev = defaults;
    setDefaults(ids);
    try {
      await axios.put(`${API}/api/slack/default-members`, { member_ids: ids }, { headers: authHeaders() });
    } catch (e) { setDefaults(prev); toast.error('Could not update default members'); }
    finally { setSavingDefaults(false); }
  };

  const connect = async (mid) => {
    setSaving(mid);
    try {
      await axios.post(`${API}/api/slack/link`, { slack_user_id: mid }, { headers: authHeaders() });
      toast.success('Slack account connected');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not connect'); }
    finally { setSaving(null); }
  };

  const disconnect = async () => {
    setSaving('__off');
    try {
      await axios.delete(`${API}/api/slack/link`, { headers: authHeaders() });
      toast.success('Slack account disconnected');
      await load();
    } catch (e) { toast.error('Could not disconnect'); }
    finally { setSaving(null); }
  };

  if (!open) return null;
  const filtered = members.filter((m) => `${m.name} ${m.handle} ${m.email || ''}`.toLowerCase().includes(q.toLowerCase()));
  const linkedMember = link ? members.find((m) => m.id === link.slack_user_id) : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="connect-slack-dialog">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#4A154B]/10 text-[#4A154B]"><Slack className="w-5 h-5" /></span>
          <div><h3 className="text-base font-black text-slate-900">Connect Slack</h3><p className="text-[11px] text-slate-500">Link your Slack so you're auto-added to client channels you export</p></div>
          <button onClick={onClose} className="ml-auto rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="connect-slack-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          {loading ? <div className="py-10 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          : err ? <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700" data-testid="connect-slack-error">{err}</div>
          : link ? (
            <div data-testid="connect-slack-linked">
              <div className="flex items-center gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <div className="min-w-0"><p className="font-bold text-slate-900 truncate">{linkedMember?.name || link.slack_user_id}</p><p className="text-xs text-slate-500 truncate">{linkedMember?.email || `Slack ID ${link.slack_user_id}`}</p></div>
              </div>
              <button onClick={disconnect} disabled={!!saving} data-testid="connect-slack-disconnect" className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-2.5 text-sm font-bold text-slate-700 hover:border-rose-300 hover:text-rose-600 disabled:opacity-50">{saving === '__off' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2Off className="w-4 h-4" />} Disconnect</button>
            </div>
          ) : (
            <div>
              <div className="relative mb-3"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your name…" data-testid="connect-slack-search" className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-[#4A154B]" /></div>
              <div className="max-h-72 overflow-y-auto -mx-1 px-1 space-y-1">
                {filtered.length === 0 ? <p className="py-6 text-center text-sm text-slate-400">No members found</p> :
                  filtered.map((m) => (
                    <button key={m.id} onClick={() => connect(m.id)} disabled={!!saving} data-testid={`connect-slack-member-${m.id}`} className="w-full flex items-center gap-3 rounded-xl px-2.5 py-2 text-left hover:bg-slate-50 disabled:opacity-50">
                      {m.image ? <img src={m.image} alt="" className="w-8 h-8 rounded-lg object-cover" /> : <span className="w-8 h-8 rounded-lg bg-[#4A154B]/10 text-[#4A154B] flex items-center justify-center font-bold text-xs">{(m.name || '?').slice(0, 1)}</span>}
                      <span className="min-w-0 flex-1"><span className="block font-semibold text-slate-900 truncate">{m.name}</span><span className="block text-[11px] text-slate-400 truncate">{m.email || '@' + m.handle}</span></span>
                      {saving === m.id ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <Check className="w-4 h-4 text-transparent" />}
                    </button>
                  ))}
              </div>
            </div>
          )}
          {!loading && !err && (
            <div className="mt-5 pt-4 border-t border-slate-100" data-testid="slack-defaults">
              <div className="flex items-center gap-2 mb-1.5">
                <Users className="w-4 h-4 text-slate-500" />
                <h4 className="text-sm font-black text-slate-900">Auto-added to new client channels</h4>
                {savingDefaults && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
              </div>
              <p className="text-[11px] text-slate-500 mb-2.5">These people are invited to every new channel created on export.</p>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {defaults.length === 0 ? <span className="text-xs text-slate-400">No default members yet.</span> :
                  defaults.map((did) => {
                    const m = members.find((x) => x.id === did);
                    return (
                      <span key={did} className="inline-flex items-center gap-1.5 rounded-full bg-[#4A154B]/10 border border-[#4A154B]/15 pl-1.5 pr-1 py-1 text-xs font-semibold text-[#4A154B]" data-testid={`slack-default-chip-${did}`}>
                        {m?.image ? <img src={m.image} alt="" className="w-4 h-4 rounded" /> : null}
                        {m?.name || did}
                        <button onClick={() => saveDefaults(defaults.filter((x) => x !== did))} disabled={savingDefaults} className="rounded-full p-0.5 hover:bg-[#4A154B]/15" data-testid={`slack-default-remove-${did}`}><X className="w-3 h-3" /></button>
                      </span>
                    );
                  })}
              </div>
              <div className="relative"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><input value={dq} onChange={(e) => setDq(e.target.value)} placeholder="Add a team member…" data-testid="slack-default-search" className="w-full rounded-xl border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:border-[#4A154B]" /></div>
              {dq && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-50">
                  {members.filter((m) => !defaults.includes(m.id) && `${m.name} ${m.handle}`.toLowerCase().includes(dq.toLowerCase())).slice(0, 8).map((m) => (
                    <button key={m.id} onClick={() => { saveDefaults([...defaults, m.id]); setDq(''); }} data-testid={`slack-default-add-${m.id}`} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50">
                      {m.image ? <img src={m.image} alt="" className="w-6 h-6 rounded" /> : <span className="w-6 h-6 rounded bg-[#4A154B]/10 text-[#4A154B] flex items-center justify-center font-bold text-[10px]">{(m.name || '?').slice(0, 1)}</span>}
                      <span className="text-sm font-medium text-slate-800 truncate">{m.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
